// hooks/useSearchTabs.ts
import { useState } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { usePermission } from '@/ui/permission';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { ContactItem, JobItem, FlowItem, InterviewItem } from './search-items';
import { SEARCH_RESULTS_PER_PAGE } from './constant';
import {
  type currentTabOptions,
  type TabConfig,
  type SearchResults,
  type UseSearchTabsReturn,
  type PermissionGuardReturn,
} from './types';

export const useSearchTabs = (
  searchResults: SearchResults
): UseSearchTabsReturn => {
  const { ats_enabled } = useKFlags();
  const { isFlowEnabled } = usePermissionGuard() as PermissionGuardReturn;
  const isContactsEnabled = usePermission({ permission: 'circles.contacts?' });
  const { isJobsVisible } = useAtsPermissions();

  const { flows, jobs, contacts, interviews } = searchResults;

  const searchTabs: TabConfig[] = [
    {
      id: 'contacts',
      text: 'Candidates',
      count: contacts.total,
      data: contacts.data,
      itemComponent: ContactItem,
      route: '/candidates',
      show: isContactsEnabled || isFlowEnabled,
    },
    {
      id: 'jobs',
      text: 'Jobs',
      count: jobs.total,
      data: jobs.data,
      itemComponent: JobItem,
      route: '/jobs',
      show: isJobsVisible && ats_enabled,
    },
    {
      id: 'flows',
      text: 'Flows',
      count: flows.total,
      data: flows.data?.slice(0, SEARCH_RESULTS_PER_PAGE),
      itemComponent: FlowItem,
      route: '/flows',
      show: isFlowEnabled,
    },
    {
      id: 'interviews',
      text: 'Interviews',
      count: interviews.total,
      data: interviews.data,
      itemComponent: InterviewItem,
      route: '/interviews/upcoming',
      show: ats_enabled,
    },
  ];

  const visibleTabs = searchTabs.filter((tab) => tab.show);

  const defaultTab = visibleTabs[0]?.id ?? 'contacts';
  const [currentTab, setCurrentTab] = useState<currentTabOptions>(defaultTab);
  const permittedTabIds = searchTabs.filter((t) => t.show).map((t) => t.id);

  return {
    searchTabs,
    visibleTabs,
    currentTab,
    setCurrentTab,
    permittedTabIds,
  };
};
